/**
 * Every tunable in one place — the WebClip's answer to the Android app's
 * `core/AppConfig.kt`.
 *
 * The numbers that appear in both files are deliberately the same numbers. They
 * define **what counts as healthy**, and two apps feeding one backend must not
 * disagree about that: a 10s timeout here and a 30s timeout there would show up
 * as "iOS devices see more dead links", which is a conclusion about the network
 * drawn from a difference in our own code.
 */

/** Base URL of our own backend. Overridable at build time — see `.env.example`. */
export const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/+$/, '') ||
  'https://apigateway.abc667.xyz/api';

/** Injected by Vite at build time (git short SHA, or a build timestamp). */
export const APP_VERSION: string =
  (import.meta.env.VITE_APP_VERSION as string | undefined) || 'dev';

/**
 * `platform` on every submitted batch. **Never `"ios"`** — see SPEC §6.
 *
 * One field is all the backend gets to tell two very different measurements
 * apart (SPEC §2). If a native iOS app ever arrives it will send `"ios"`, and
 * the day that happens nobody should have to work out which rows came from
 * which engine by looking at timestamps.
 */
export const PLATFORM = 'ios-webclip' as const;

/**
 * Timeout for probing one external link.
 *
 * Same value as Android's `CHECK_TIMEOUT_SECONDS`. Do not raise it to "help"
 * slow regions — a link that answers only after 15s is unhealthy by definition
 * on both apps, and changing that here alone silently changes the data.
 */
export const PROBE_TIMEOUT_MS = 10_000;

/**
 * Timeout for our own backend (`device-api/*`).
 *
 * Longer than the probe on purpose, matching Android's `API_TIMEOUT_SECONDS`:
 * these calls cross continents from handsets in Brazil and China, and at 10s a
 * fetch or a submit fails spuriously on a device whose network is fine. Nothing
 * about health classification depends on this number.
 */
export const API_TIMEOUT_MS = 20_000;

/** Timeout for the keyless ipwho.is lookup. Best-effort — a miss costs a label, not a run. */
export const IP_INFO_TIMEOUT_MS = 8_000;

/**
 * How many links are probed at once. Four, matching Android.
 *
 * On a mobile radio a wide fan-out does not go out in parallel so much as queue
 * behind itself, and the requests that wait longest are the ones that time out —
 * recording a link unhealthy because of our own burst. Four also keeps what a
 * carrier's middlebox sees from one handset modest.
 */
export const PROBE_CONCURRENCY = 4;

/**
 * What a cycle runs on when `GET /device-api/config` cannot be reached.
 *
 * Deliberately **not** one round, for the same reason as Android: the
 * false-positive guard is why the multi-round design exists, and a broken
 * network is both when config calls fail *and* when false positives are most
 * common. Switching the guard off exactly then would quietly degrade the data.
 */
export const DEFAULT_CHECK_ROUNDS = 3;
export const DEFAULT_ROUND_INTERVAL_SECONDS = 3;
export const DEFAULT_CHECK_INTERVAL_MINUTES = 60;

/** Bounds on what the server may ask for (SPEC §7.2, mirrors Android's clamps). */
export const MIN_CHECK_ROUNDS = 1;
export const MAX_CHECK_ROUNDS = 5;
export const MAX_ROUND_INTERVAL_SECONDS = 300;

/**
 * Floor on the repeat period, in minutes.
 *
 * **A policy choice, not a platform limit** (SPEC §9). Android's floor is 15
 * because WorkManager silently rounds anything shorter up; a foreground
 * `setTimeout` has no such floor and would happily hammer the target domains and
 * the battery every 30 seconds if someone typed `0.5` on the server.
 */
export const MIN_CHECK_INTERVAL_MINUTES = 5;

/**
 * Ceiling on the **total** time one cycle may spend idling between rounds.
 *
 * Clamping each value alone is not enough: `rounds=5` with `roundInterval=300s`
 * is 20 minutes of pure waiting, and this app only runs while someone is looking
 * at the screen. The product is what has to be bounded. Same rule and same
 * number as Android's `MAX_TOTAL_ROUND_GAP_SECONDS`.
 */
export const MAX_TOTAL_ROUND_GAP_SECONDS = 180;

/**
 * Wall-clock ceiling on one whole cycle's checking phase.
 *
 * Nothing in iOS kills this run the way WorkManager kills a worker, so this is
 * not a survival measure — it is what stops one pathological list from eating
 * the next cycle and every cycle after it. On expiry the run submits what it has
 * already proven, which is the honest outcome; see `aggregate`'s `dropped`.
 */
export const RUN_DEADLINE_MS = 8 * 60 * 1000;

/**
 * How long a cycle may wait for the device to look online before giving up.
 *
 * Shorter than Android's 4 minutes because this app only runs in the foreground:
 * a person is holding the phone, and a screen that says "waiting for network"
 * for four minutes reads as a hang.
 */
export const ONLINE_WAIT_TIMEOUT_MS = 60_000;
export const ONLINE_WAIT_POLL_MS = 2_000;

/**
 * Per-result error budget, in characters (SPEC §7.1).
 *
 * A batch carries one row per link and hundreds of links, so this is a per-row
 * budget rather than a display limit. Same reasoning as Android's
 * `MAX_TRACE_CHARS`, with the roomier number the spec asks for.
 */
export const MAX_ERROR_CHARS = 160;

/** Keyless third-party geo/carrier lookup. **Never** receives the device key. */
export const IP_INFO_URL = 'https://ipwho.is/';

/**
 * What goes on the wire when we genuinely do not know.
 *
 * A string, never null and never a guess: the backend stores these as facts
 * about the device, so "we do not know" has to be sayable. Matches Android's
 * `MeasurementContext.UNKNOWN`.
 */
export const UNKNOWN = 'unknown';

/**
 * A value fit to print on a screen, or `null` for "we do not know".
 *
 * The boundary between the wire vocabulary and the UI's, in one tested place.
 * `UNKNOWN` is filtered out on purpose: it exists so the backend receives a real
 * non-blank string rather than a `null` it has to guess at (SPEC §8), and it is
 * English. Letting it through would print "unknown" untranslated on a
 * Vietnamese phone and — worse — would read as a carrier, region or IP actually
 * called that. The UI's spelling of the same idea is the `value_unknown` key, so
 * callers write `displayable(x) ?? t('value_unknown')`.
 *
 * Blank and whitespace-only values go the same way: a label rendered as an empty
 * gap tells an operator nothing, while "Unknown" tells them the lookup failed.
 */
export function displayable(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (trimmed === undefined || trimmed === '' || trimmed === UNKNOWN) return null;
  return trimmed;
}
